import {
  GetExamLocationRequestInfoDocument,
  useDeleteExamRoomRequestInfoByIdMutation,
  useDuplicateExamRoomRequestInfoMutation,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate, useParams } from '@exam-center/router';
import {
  AddIcon,
  BusinessOutlinedIcon,
  Button,
  ConfirmationModal,
  ContentCopyRoundedIcon,
  DeleteOutlineIcon,
  EditOutlinedIcon,
  Flex,
  PaperSection,
  Text,
  VisibilityOutlinedIcon,
  useTheme,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission, WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import ExamRoomCard from '../../components/exam-room-card/exam-room-card';
import useExamLocationRequestDetails from '../../hooks/use-exam-location-request-details/use-exam-location-request-details';
import { ExamRoomCreateModal } from '../exam-room-create-modal';

export default function ExamLocationRequestRoomSection() {
  const [isOpen, { open, close }] = useDisclosure();
  const [
    isDeleteExamRoomModalOpen,
    { open: openDeleteExamRoomModal, close: closeDeleteExamRoomModal },
  ] = useDisclosure();

  const navigate = useNavigate();
  const theme = useTheme();

  const { requestId, examCenterCode } = useParams(
    '/:examCenterCode/exam-location/requests/draft/:requestId',
  );

  const { sortedExamRooms } = useExamLocationRequestDetails();

  const [deleteExamRoom, { loading: isDeleting }] = useDeleteExamRoomRequestInfoByIdMutation();
  const [duplicateExamRoom] = useDuplicateExamRoomRequestInfoMutation();

  const [selectedExamRoomId, setSelectedExamRoomId] = useState<string | null>(null);

  const { showSnackbar } = useSnackbar();

  const handleDeleteExamRoom = async (examRoomId: string | null) => {
    if (!examRoomId) {
      return;
    }
    await deleteExamRoom({
      variables: {
        deleteExamRoomRequestInfoByIdInput: {
          id: examRoomId,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ลบห้องสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        closeDeleteExamRoomModal();
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถลบห้องสอบได้',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamLocationRequestInfoDocument],
    });
  };

  const handleDuplicateExamRoom = async (examRoomId: string) => {
    await duplicateExamRoom({
      variables: {
        duplicateExamRoomRequestInfoInput: {
          examRoomRequestInfoId: examRoomId,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ทำสำเนาห้องสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถทำสำเนาห้องสอบได้',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamLocationRequestInfoDocument],
    });
  };

  const hasUpdatePermission = useHasPermission([
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
  ]);
  const hasCreatePermission = useHasPermission([
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.CREATE,
  ]);
  const hasDeletePermission = useHasPermission([
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.DELETE,
  ]);

  return (
    <>
      <PaperSection
        title='ข้อมูลห้องสอบ'
        endAdornment={
          <WithPermission
            requiredPermission={[
              APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.CREATE,
            ]}
          >
            <Button variant='contained' onClick={open} startIcon={<AddIcon />}>
              เพิ่มห้องสอบ
            </Button>
          </WithPermission>
        }
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '10px',
            padding: (theme) => theme.spacing(6),
            alignItems: 'center',
          }}
        >
          {sortedExamRooms.length === 0 ? (
            <Flex
              sx={{
                flexDirection: 'column',
                gap: (theme) => theme.spacing(4),
                alignItems: 'center',
              }}
            >
              <BusinessOutlinedIcon
                sx={(theme) => ({
                  color: theme.palette.grey[400],
                  width: theme.spacing(12),
                  height: theme.spacing(12),
                })}
              />
              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: (theme) => theme.spacing(2),
                  alignItems: 'center',
                }}
              >
                <Text variant='h5' fontWeight='semibold'>
                  ยังไม่มีข้อมูลห้องสอบ
                </Text>
                <Text variant='body1'>
                  กรุณาเพิ่มห้องสอบอย่างน้อย 1 ห้อง ก่อนส่งคำขออนุมัติสถานที่สอบ
                </Text>
              </Flex>
            </Flex>
          ) : (
            sortedExamRooms.map((examRoom, idx) => (
              <ExamRoomCard
                key={examRoom.id}
                floor={examRoom.floor}
                orderNumber={idx + 1}
                roomName={examRoom.name}
                roomNumber={examRoom.roomNumber || ''}
                seatCount={examRoom.seatCount}
                menuOptions={[
                  {
                    icon: hasUpdatePermission ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />,
                    label: hasUpdatePermission ? 'แก้ไขห้องสอบ' : 'ดูข้อมูลห้องสอบ',
                    onClick: () =>
                      navigate(
                        '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId',
                        {
                          params: {
                            examCenterCode,
                            requestId,
                            roomRequestId: examRoom.id,
                          },
                        },
                      ),
                  },
                  ...(hasCreatePermission
                    ? [
                        {
                          icon: <ContentCopyRoundedIcon />,
                          label: 'ทำสำเนาห้องสอบ',
                          onClick: () => handleDuplicateExamRoom(examRoom.id),
                        },
                      ]
                    : []),
                  ...(hasDeletePermission
                    ? [
                        {
                          icon: <DeleteOutlineIcon />,
                          label: 'ลบห้องสอบ',
                          onClick: () => {
                            setSelectedExamRoomId(examRoom.id);
                            openDeleteExamRoomModal();
                          },
                          sx: {
                            color: theme.palette.error.main,
                          },
                        },
                      ]
                    : []),
                ]}
              />
            ))
          )}
        </Flex>
      </PaperSection>
      <ExamRoomCreateModal open={isOpen} onClose={close} locationRequestId={requestId} />
      <ConfirmationModal
        title='ลบห้องสอบ?'
        content={<Text>คุณกำลังจะลบห้องสอบ เมื่อลบแล้วจะไม่สามารถนำข้อมูลกลับมาได้อีก</Text>}
        open={isDeleteExamRoomModalOpen}
        onClose={closeDeleteExamRoomModal}
        onDiscard={closeDeleteExamRoomModal}
        acceptButtonProps={{
          color: 'error',
        }}
        acceptText='ลบห้องสอบ'
        discardText='ยกเลิก'
        onAccept={() => handleDeleteExamRoom(selectedExamRoomId)}
        loading={isDeleting}
      />
    </>
  );
}
